// components/EditShiftModal.jsx
import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";

const EditShiftModal = ({
  open,
  onClose,
  editForm,
  setEditForm,
  today,
  timeOptions,
  currentHour,
  currentMinute,
  minimumHour,
  maximumHour,
  handleSaveEdit,
  editingIndex,
  updateShift,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open]);

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: isMobile ? '90%' : isTablet ? '80%' : 600,
    maxWidth: '100%',
    maxHeight: '90vh',
    bgcolor: "#fff",
    borderRadius: 4,
    boxShadow: "0 8px 30px rgba(0,0,0,0.1)",
    p: isMobile ? 2 : 4,
    overflowY: 'auto',
  };

  const unifiedFieldStyle = {
    height: "48px",
    width: '100%',
    borderRadius: "8px",
    backgroundColor: "white",
    "& .MuiOutlinedInput-root": {
      border: "1px solid transparent",
      borderRadius: "8px",
      transition: "border 0.2s ease",
      height: '100%',
      "&:hover": {
        border: "1px solid transparent",
      },
      "&.Mui-focused": {
        border: "2px solid #1976d2",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        display: "none",
      },
    },
    "& .MuiInputLabel-root": {
      backgroundColor: "#fff",
      padding: "0 4px",
      zIndex: 1,
      fontSize: isMobile ? '0.875rem' : '1rem',
    },
    "& .MuiInputBase-input": {
      fontSize: isMobile ? '0.875rem' : '1rem',
      height: '100%',
      padding: isMobile ? '8px 14px' : '14px',
    },
  };

  const buttonStyle = {
    minWidth: isMobile ? 'auto' : 120,
    padding: isMobile ? '6px 12px' : '8px 16px',
    fontSize: isMobile ? '0.8125rem' : '0.875rem',
  };

  return (
    <Modal 
      open={open} 
      onClose={onClose}
      disableScrollLock={false}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(2px)',
        },
      }}
    >
      <Box sx={modalStyle}>
        <Typography 
          variant="h5" 
          mb={3} 
          fontWeight="bold" 
          color="#06468c"
          fontSize={isMobile ? '1.25rem' : '1.5rem'}
        >
          Edit Shift
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              variant="outlined"
              value={editForm.startDate}
              InputLabelProps={{ 
                shrink: true,
                style: {
                  fontSize: isMobile ? '0.875rem' : '1rem',
                }
              }}
              inputProps={{
                min: today,
                style: {
                  height: "48px",
                  boxSizing: "border-box",
                  padding: "0 14px",
                  fontSize: isMobile ? '0.875rem' : '1rem',
                },
              }}
              onChange={(e) =>
                setEditForm({ ...editForm, startDate: e.target.value })
              }
              sx={unifiedFieldStyle}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth sx={{ 
              ...unifiedFieldStyle, 
              height: 'auto',
              border: '1px solid #c4c4c4',
              borderRadius: '8px',
              '&:hover': {
                border: '1px solid #000',
              },
              '&.Mui-focused': {
                border: '2px solid #1976d2',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}>
              <InputLabel sx={{ 
                fontSize: isMobile ? '0.875rem' : '1rem',
                '&.Mui-focused': {
                  color: 'rgba(0, 0, 0, 0.6)',
                },
              }}>
                Start Time
              </InputLabel>
              <Select
                value={editForm.startTime}
                onChange={(e) =>
                  setEditForm({ ...editForm, startTime: e.target.value })
                }
                label="Start Time"
                sx={{
                  ...unifiedFieldStyle,
                  height: '48px',
                  '& .MuiSelect-select': {
                    fontSize: isMobile ? '0.875rem' : '1rem',
                    padding: isMobile ? '8px 32px 8px 14px' : '14px 32px 14px 14px',
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                <MenuItem value="">Select time</MenuItem>
                {timeOptions.map((time) => {
                  const isToday = editForm.startDate === today;
                  const [hour, minute] = time.split(":").map(Number);
                  const timeInMinutes = hour * 60 + minute;
                  const currentTimeInMinutes = currentHour * 60 + currentMinute;
                  const isInFuture =
                    !isToday || timeInMinutes > currentTimeInMinutes;

                  return isInFuture ? (
                    <MenuItem 
                      key={time} 
                      value={time}
                      sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
                    >
                      {time}
                    </MenuItem>
                  ) : null;
                })}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label={`No. of Hours (${minimumHour}-${maximumHour})`}
              step="0.5"
              value={editForm.duration}
              onChange={(e) => {
                const rawValue = e.target.value;
                if (rawValue === "") {
                  setEditForm({ ...editForm, duration: "" });
                  return;
                }
                const isMultipleOfHalf = (rawValue * 10) % 5 === 0;
                if (isMultipleOfHalf) {
                  setEditForm({ ...editForm, duration: rawValue });
                }
              }}
              onWheel={(e) => e.target.blur()}
              inputProps={{
                min: minimumHour,
                max: maximumHour,
                style: {
                  padding: isMobile ? '8px 14px' : '14px',
                  height: 'auto',
                  fontSize: isMobile ? '0.875rem' : '1rem',
                },
              }}
              sx={unifiedFieldStyle}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Rate Offered (per hour)"
              value={editForm.rateOffered}
              onChange={(e) =>
                setEditForm({
                  ...editForm,
                  rateOffered: e.target.value,
                })
              }
              onWheel={(e) => e.target.blur()}
              inputProps={{
                min: 0,
                style: {
                  padding: isMobile ? '8px 14px' : '14px',
                  height: 'auto',
                  fontSize: isMobile ? '0.875rem' : '1rem',
                },
              }}
              sx={unifiedFieldStyle}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography 
              variant="subtitle1" 
              mb={1}
              fontSize={isMobile ? '0.9375rem' : '1rem'}
            >
              Camp Duty
            </Typography>
            <RadioGroup
              row
              name="campDuty"
              value={editForm.campDuty}
              onChange={(e) =>
                setEditForm({
                  ...editForm,
                  campDuty: e.target.value === "true",
                })
              }
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: isMobile ? '0.875rem' : '1rem',
                }
              }}
            >
              <FormControlLabel 
                value={false} 
                control={<Radio size={isMobile ? 'small' : 'medium'} />} 
                label="No" 
              />
              <FormControlLabel 
                value={true} 
                control={<Radio size={isMobile ? 'small' : 'medium'} />} 
                label="Yes" 
              />
            </RadioGroup>
          </Grid>
        </Grid>

        <Box 
          mt={4} 
          display="flex" 
          justifyContent="flex-end" 
          gap={2}
          flexWrap="wrap"
        >
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              ...buttonStyle,
              borderColor: "#d63384",
              color: "#d63384",
              "&:hover": {
                borderColor: "#c2185b",
                backgroundColor: "#fce4ec",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSaveEdit(editingIndex, editForm)}
            disabled={updateShift.isPending}
            sx={{ 
              ...buttonStyle,
              backgroundColor: "#06468c",
              '&:hover': {
                backgroundColor: '#05376e',
              },
            }}
          >
            {updateShift.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditShiftModal;
